import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SchoolOutlinedIcon from '@material-ui/icons/SchoolOutlined';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import EditorLesson from './EditorLesson';
import { readLessonProgress, writeLessonProgress, LESSON_STEPS } from './lessonState';
import styles from './Onboarding.module.scss';

const OnboardingForUser = ({ storageKey, disabled }) => {
  const t = useLocalizedMessage();
  const [progress, setProgress] = useState(() => readLessonProgress(storageKey));
  const [session, setSession] = useState(null);
  const nextSession = useRef(0);
  const [menu, setMenu] = useState(null);
  const [topic, setTopic] = useState(null);
  const persist = useCallback(value => {
    setProgress(value);
    writeLessonProgress(storageKey, value);
  }, [storageKey]);
  const onProgress = useCallback(step => persist({ step, status: step === LESSON_STEPS.length ? 'completed' : 'paused' }), [persist]);
  const canResume = progress?.status === 'paused' || (progress?.status === 'dismissed' && progress.step > 0);
  const start = (restart = false) => {
    setMenu(null);
    nextSession.current += 1;
    setSession({ id: nextSession.current, step: !restart && canResume ? progress.step : 0 });
  };
  return <div className={styles.entry} data-testid="new-editor-learning">
    <Button size="small" color="primary" variant="outlined" startIcon={<SchoolOutlinedIcon />} disabled={disabled} onClick={() => start()}>{t(canResume ? 'experience.lesson.resume' : progress?.status === 'completed' ? 'experience.lesson.restart' : 'experience.lesson.start')}</Button>
    <Button size="small" startIcon={<HelpOutlineIcon />} onClick={event => setMenu(event.currentTarget)} aria-haspopup="menu" aria-expanded={Boolean(menu)}>{t('experience.lesson.help')}</Button>
    <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
      {progress && <MenuItem disabled={disabled} onClick={() => start(true)}>{t('experience.lesson.restart')}</MenuItem>}
      {['navigation', 'screens', 'saving'].map(value => <MenuItem key={value} onClick={() => { setMenu(null); setTopic(value); }}>{t(`experience.lesson.help.${value}.title`)}</MenuItem>)}
    </Menu>
    <Dialog open={Boolean(topic)} onClose={() => setTopic(null)} maxWidth="sm" fullWidth aria-labelledby="editor-help-title">
      {topic && <>
        <DialogTitle id="editor-help-title">{t(`experience.lesson.help.${topic}.title`)}</DialogTitle>
        <DialogContent><p className={styles.helpText}>{t(`experience.lesson.help.${topic}.body`)}</p></DialogContent>
        <DialogActions><Button onClick={() => setTopic(null)}>{t('common.close')}</Button></DialogActions>
      </>}
    </Dialog>
    {session && <EditorLesson key={session.id} initialStep={session.step} onProgress={onProgress}
      onClose={() => setSession(null)} onRestart={() => start(true)} />}
  </div>;
};

const EditorOnboarding = ({ shopId, disabled = false }) => {
  const user = useSelector(state => state.auth?.authUser || state.auth?.authorization?.account?.id);
  const userId = typeof user === 'string' || typeof user === 'number' ? user : user?.id || 'browser';
  const storageKey = `kea.search-experience.lesson.v1:${encodeURIComponent(userId)}:${encodeURIComponent(shopId)}`;
  return <OnboardingForUser key={storageKey} storageKey={storageKey} disabled={disabled} />;
};
export default EditorOnboarding;
